import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Redirect } from 'react-router-dom';

import ErrorBoundary from 'src/components/ErrorBoundary';
import Meta from 'src/components/Meta';
import Notification from 'src/components/Notification';
import { setLoginRedirect } from 'src/utils/data/loginRedirect';
import { useAuth } from 'src/hooks/app';
import * as ROUTE from 'src/routes/routes';
import AccessDeniedCustomPage from 'src/pages/AccessDeniedCustomPage';
import AccessDeniedPage from 'src/pages/AccessDeniedPage';
import CurrentUserPermissionsContext from 'src/utils/context/CurrentUserPermissionsContext';
import s from './EmptyPrivateLayout.styles';

const EmptyPrivateLayout = ({ children, path }) => {
  const [authorized] = useAuth();
  const expanded = false;

  const { permissions, loading } = useContext(CurrentUserPermissionsContext);

  const urlParameter = queryString.parse(window.location.search);
  const requestingUrl = urlParameter.requesting_url;

  if (!authorized) {
    setLoginRedirect(window.location.pathname + window.location.search);
    return <Redirect to={ROUTE.HOME} />;
  }
  const userPagePermission =
    !loading &&
    permissions &&
    !!(permissions || []).find(
      a => a.resourceName === 'User' && a.name === 'read',
    );
  const jobPagePermission =
    !loading &&
    permissions &&
    !!(permissions.currentUserPermissions || []).find(
      a => a.resourceName === 'Job' && a.name === 'read',
    );
  const mdmPagePermission =
    !loading &&
    permissions &&
    !!(permissions.currentUserPermissions || []).find(
      a => a.resourceName === 'MasterData' && a.name === 'read',
    );

  const resourcePath = path.split('/')[1];

  const resourcePathToPermission = {
    profile: true,
    '': true,
    'master-data': mdmPagePermission,
    jobs: jobPagePermission,
    users: userPagePermission,
    graphiql: true,
    voyager: true,

    /*
     * Code for investigation results for PHX-2689. To be removed after acceptance.
     */
    'test-image-editing': true,
  };

  const hasPermission = loading ? null : resourcePathToPermission[resourcePath];

  const content = () => {
    if (!loading && hasPermission) {
      return (
        <s.Content collapsed={expanded} noPadding={path === '/'}>
          {children}
        </s.Content>
      );
    }

    if (!loading && !hasPermission && requestingUrl) {
      return (
        <s.Content collapsed={expanded}>
          <AccessDeniedCustomPage requestingUrl={requestingUrl} />
        </s.Content>
      );
    }
    
    if (!loading && !hasPermission) {
      return (
        <s.Content collapsed={expanded}>
          <AccessDeniedPage />
        </s.Content>
      );
    }

    return null;
  };

  return (
    <s.Layout>
      <ErrorBoundary>
        <Meta />
        {content()}
        <Notification />
      </ErrorBoundary>
    </s.Layout>
  );
};

EmptyPrivateLayout.propTypes = {
  path: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
};

export default React.memo(EmptyPrivateLayout);
